// 統計・分析ダッシュボード
import React, { useState, useEffect } from 'react';
import apiClient, { getApiUrl } from '../../utils/apiClient';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  TrendingUp, Users, ShoppingCart, Award, Calendar,
  DollarSign, Clock, Target, Activity, Home
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import PageContainer from '../layout/PageContainer';
interface AnalyticsData {
  pointCard: {
    members: {
      totalMembers: number;
      newMembers: number;
      avgPoints: number;
    };
    points: {
      totalPointsAwarded: number;
      activeMembers: number;
    };
    coupons: {
      totalCouponsIssued: number;
      couponsUsed: number;
      redemptionRate: number;
    };
  };
  ranks: Array<{
    current_rank: string;
    count: number;
    percentage: string;
    avgPoints: number;
    avgVisits: number;
  }>;
  takeout: {
    basic: {
      totalOrders: number;
      totalRevenue: number;
      avgOrderValue: number;
      completionRate: string;
    };
    topItems: Array<{
      name: string;
      quantity: number;
      revenue: number;
    }>;
    paymentMethods: Array<{
      payment_method: string;
      count: number;
      revenue: number;
    }>;
  };
  customerBehavior: {
    visitFrequency: {
      daily: number;
      weekly: number;
      biweekly: number;
      monthly: number;
      irregular: number;
    };
    customerSegments: {
      vip: number;
      loyal: number;
      potential: number;
      new: number;
      dormant: number;
    };
  };
  forecast: {
    forecast: Array<{
      date: string;
      predictedOrders: number;
      predictedRevenue: number;
    }>;
  };
}

const Analytics: React.FC = () => {
  const navigate = useNavigate();
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'customers' | 'sales' | 'forecast'>('overview');

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${getApiUrl()}/line/analytics/comprehensive?start=${dateRange.start}&end=${dateRange.end}`
      );
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      const data = await response.json();
      setAnalyticsData(data);
    } catch (error) {
      console.error('分析データ取得エラー:', error);
      setAnalyticsData(null);
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#FF6B35', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8'];

  const getRankColor = (rank: string) => {
    const colors = {
      bronze: '#CD7F32',
      silver: '#C0C0C0',
      gold: '#FFD700',
      platinum: '#E5E4E2'
    };
    return colors[rank] || '#999';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4">分析データを読み込み中...</p>
        </div>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="text-xl text-red-500 mb-4">
            データの読み込みに失敗しました
          </div>
          <p className="text-gray-600">
            LINE分析APIが実装されていない可能性があります。<br />
            システム管理者にお問い合わせください。
          </p>
        </div>
      </div>
    );
  }

  return (
    <PageContainer>{/* ヘッダー */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-800">📊 統計・分析ダッシュボード</h1>
          <button
            onClick={() => navigate('/home')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Home className="w-5 h-5" />
            ホーム
          </button>
        </div>
        <div className="flex gap-4 mt-4">
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
            className="px-4 py-2 border rounded-lg"
          />
          <span className="self-center">〜</span>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
            className="px-4 py-2 border rounded-lg"
          />
        </div>
      </div>

      {/* タブナビゲーション */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-6 py-2 rounded-lg font-semibold ${
            activeTab === 'overview' 
              ? 'bg-orange-500 text-white' 
              : 'bg-white text-gray-700'
          }`}
        >
          概要
        </button>
        <button
          onClick={() => setActiveTab('customers')}
          className={`px-6 py-2 rounded-lg font-semibold ${
            activeTab === 'customers' 
              ? 'bg-orange-500 text-white' 
              : 'bg-white text-gray-700'
          }`}
        >
          顧客分析
        </button>
        <button
          onClick={() => setActiveTab('sales')}
          className={`px-6 py-2 rounded-lg font-semibold ${
            activeTab === 'sales' 
              ? 'bg-orange-500 text-white' 
              : 'bg-white text-gray-700'
          }`}
        >
          売上分析
        </button>
        <button
          onClick={() => setActiveTab('forecast')}
          className={`px-6 py-2 rounded-lg font-semibold ${
            activeTab === 'forecast' 
              ? 'bg-orange-500 text-white' 
              : 'bg-white text-gray-700'
          }`}
        >
          予測
        </button>
      </div>

      {/* 概要タブ */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {/* KPIカード */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <Users className="text-blue-500" size={24} />
              <span className="text-2xl font-bold">
                {analyticsData?.pointCard?.members?.totalMembers || 0}
              </span>
            </div>
            <p className="text-gray-600">総会員数</p>
            <p className="text-sm text-green-500 mt-1">
              +{analyticsData?.pointCard?.members?.newMembers || 0} 新規
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <ShoppingCart className="text-green-500" size={24} />
              <span className="text-2xl font-bold">
                {analyticsData?.takeout?.basic?.totalOrders || 0}
              </span>
            </div>
            <p className="text-gray-600">注文数</p>
            <p className="text-sm text-gray-500 mt-1">
              完了率 {analyticsData?.takeout?.basic?.completionRate || 0}%
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="text-orange-500" size={24} />
              <span className="text-2xl font-bold">
                ¥{(analyticsData?.takeout?.basic?.totalRevenue || 0).toLocaleString()}
              </span>
            </div>
            <p className="text-gray-600">売上高</p>
            <p className="text-sm text-gray-500 mt-1">
              平均 ¥{Math.round(analyticsData?.takeout?.basic?.avgOrderValue || 0).toLocaleString()}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-2">
              <Award className="text-purple-500" size={24} />
              <span className="text-2xl font-bold">
                {analyticsData?.pointCard?.coupons?.redemptionRate || 0}%
              </span>
            </div>
            <p className="text-gray-600">クーポン利用率</p>
            <p className="text-sm text-gray-500 mt-1">
              {analyticsData?.pointCard?.coupons?.couponsUsed || 0} / {analyticsData?.pointCard?.coupons?.totalCouponsIssued || 0}
            </p>
          </div>
        </div>
      )}

      {/* 顧客分析タブ */}
      {activeTab === 'customers' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* ランク分布 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold mb-4">会員ランク分布</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={analyticsData?.ranks || []}
                  dataKey="count"
                  nameKey="current_rank"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ current_rank, percentage }) => 
                    `${current_rank}: ${percentage}%`
                  }
                >
                  {(analyticsData?.ranks || []).map((entry, index) => (
                    <Cell key={index} fill={getRankColor(entry.current_rank)} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* 来店頻度 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold mb-4">来店頻度分布</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={[
                { name: '毎日', value: analyticsData?.customerBehavior?.visitFrequency?.daily || 0 },
                { name: '週1回', value: analyticsData?.customerBehavior?.visitFrequency?.weekly || 0 },
                { name: '2週に1回', value: analyticsData?.customerBehavior?.visitFrequency?.biweekly || 0 },
                { name: '月1回', value: analyticsData?.customerBehavior?.visitFrequency?.monthly || 0 },
                { name: '不定期', value: analyticsData?.customerBehavior?.visitFrequency?.irregular || 0 }
              ]}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#FF6B35" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* 顧客セグメント */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold mb-4">顧客セグメント</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="font-semibold">VIP顧客</span>
                <span className="text-2xl font-bold text-purple-600">
                  {analyticsData.customerBehavior.customerSegments.vip}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-semibold">常連顧客</span>
                <span className="text-2xl font-bold text-blue-600">
                  {analyticsData.customerBehavior.customerSegments.loyal}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-semibold">潜在顧客</span>
                <span className="text-2xl font-bold text-green-600">
                  {analyticsData.customerBehavior.customerSegments.potential}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-semibold">新規顧客</span>
                <span className="text-2xl font-bold text-orange-600">
                  {analyticsData.customerBehavior.customerSegments.new}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-semibold">休眠顧客</span>
                <span className="text-2xl font-bold text-gray-600">
                  {analyticsData.customerBehavior.customerSegments.dormant}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 売上分析タブ */}
      {activeTab === 'sales' && (
        <div className="space-y-6">
          {/* 人気商品ランキング */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold mb-4">人気商品TOP10</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">順位</th>
                    <th className="text-left py-2">商品名</th>
                    <th className="text-right py-2">販売数</th>
                    <th className="text-right py-2">売上高</th>
                  </tr>
                </thead>
                <tbody>
                  {analyticsData.takeout.topItems.map((item, index) => (
                    <tr key={index} className="border-b">
                      <td className="py-2">{index + 1}</td>
                      <td className="py-2">{item.name}</td>
                      <td className="text-right py-2">{item.quantity}</td>
                      <td className="text-right py-2">¥{item.revenue.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 支払い方法分析 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold mb-4">支払い方法別売上</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={analyticsData.takeout.paymentMethods}
                  dataKey="revenue"
                  nameKey="payment_method"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label
                >
                  {analyticsData.takeout.paymentMethods.map((entry, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* 予測タブ */}
      {activeTab === 'forecast' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold mb-4">7日間売上予測</h3>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={analyticsData.forecast.forecast}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="predictedOrders"
                stroke="#FF6B35"
                name="予測注文数"
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="predictedRevenue"
                stroke="#4ECDC4"
                name="予測売上"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </PageContainer>
  );
};

export default Analytics;